import React, { useState, useEffect, useCallback } from 'react';
import PageHeader from '../components/Common/PageHeader';
import SlideoutPanel from '../components/Common/SlideoutPanel';
import { useSlideout } from '../hooks/useSlideout';
import WidgetStepReorder from '../components/WidgetManager/WidgetStepReorder';
import '../styles/WidgetStepReorder.css';
import apiService from '../services/api';

// Base URL used to construct embed code snippets
const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5022/api').replace('/api', '');
const BOOK_BASE = process.env.REACT_APP_BOOK_URL || 'http://localhost:3001';

// Map a BookingWidgetDTO from the API to the shape the UI expects
const transformWidget = (w) => ({
  id: w.id,
  widgetKey: w.widgetKey,
  name: w.name || '',
  description: w.description || '',
  widgetTitle: w.widgetTitle || '',
  widgetSubtitle: w.widgetSubtitle || '',
  type: 'full-booking',
  status: w.isActive ? 'active' : 'inactive',
  createdDate: w.createdAt ? new Date(w.createdAt).toISOString().split('T')[0] : '',
  lastUpdated: w.createdAt ? new Date(w.createdAt).toISOString().split('T')[0] : '',
  analytics: {
    totalViews: w.totalViews || 0,
    totalConversions: w.totalBookings || 0,
    conversionRate: Number((w.conversionRate || 0).toFixed(1)),
    abandonment: {},
    revenue: 0,
    averageBookingValue: 0,
  },
  deployment: {
    liveUrl: w.widgetKey ? `${API_BASE}/api/widget/${w.widgetKey}/embed.html` : '',
    testUrl: w.widgetKey ? `${API_BASE}/api/widget/${w.widgetKey}/embed.html` : '',
    embedCode: w.widgetKey
      ? `<script src="${API_BASE}/api/widget/loader.js" data-widget="${w.widgetKey}"></script>`
      : '',
    domains: w.allowedDomains
      ? w.allowedDomains.split(',').map(d => d.trim()).filter(Boolean)
      : [],
    version: '1.0.0',
  },
  configuration: {
    branding: {
      primaryColor: w.theme?.primaryColor || w.primaryColor || '#0e73e4',
      secondaryColor: w.theme?.secondaryColor || w.secondaryColor || '#4ae7cd',
      fontFamily: w.fontFamily || 'Inter',
    },
    allowedServices: w.specificServiceIds
      ? w.specificServiceIds.split(',').map(s => s.trim())
      : ['All'],
    allowedPractitioners: w.specificPractitionerIds
      ? w.specificPractitionerIds.split(',').map(s => s.trim())
      : ['All'],
    allowedLocations: w.specificLocationIds
      ? w.specificLocationIds.split(',').map(s => s.trim())
      : ['All'],
    features: {
      socialLogin: w.allowGuestBooking ?? true,
      paymentIntegration: w.enablePaymentCollection ?? false,
      multiStep: true,
      notifications: w.sendConfirmationEmail ?? true,
    },
    restrictions: {
      advanceBookingDays: w.maxAdvanceBookingDays ?? 30,
      cancellationHours: w.minAdvanceBookingHours ?? 24,
    },
  },
});


// ─────────────────────────────────────────────────────────────────────────────
// MultiSelectList — top-level component for selecting a subset of items.
// null = "All" (no restriction); array of IDs = specific selection only.
// ─────────────────────────────────────────────────────────────────────────────
const MultiSelectList = ({ items, selectedIds, onChange }) => {
  const isAll = !selectedIds || selectedIds.length === 0;

  const handleSelectAll = () => {
    if (!isAll) onChange(null);
  };

  const handleItemToggle = (itemId) => {
    if (isAll) {
      // Switching from All: deselect just this one, keep all others
      const others = items.map(i => i.id).filter(id => id !== itemId);
      onChange(others.length > 0 ? others : null);
    } else {
      const isChecked = selectedIds.includes(itemId);
      const next = isChecked
        ? selectedIds.filter(id => id !== itemId)
        : [...selectedIds, itemId];
      // Auto-revert to All when every item is individually checked
      onChange(next.length === 0 || next.length === items.length ? null : next);
    }
  };

  const isItemChecked = (itemId) => isAll || selectedIds.includes(itemId);

  return (
    <div className="multiselect-list">
      <label className="widget-form-checkbox-label multiselect-select-all">
        <input
          type="checkbox"
          checked={isAll}
          onChange={handleSelectAll}
          readOnly={isAll}
        />
        <strong>All (no restriction)</strong>
      </label>
      <div className="multiselect-items">
        {items.length === 0
          ? <span className="multiselect-empty">No items available</span>
          : items.map(item => (
              <label key={item.id} className="widget-form-checkbox-label">
                <input
                  type="checkbox"
                  checked={isItemChecked(item.id)}
                  onChange={() => handleItemToggle(item.id)}
                />
                <span>{item.name}</span>
              </label>
            ))
        }
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// WidgetForm — top-level component (MUST stay outside WidgetManagerView).
// Defining it inside the parent causes React to create a new component type on
// every render, which unmounts/remounts the form and loses focus on each keypress.
// ─────────────────────────────────────────────────────────────────────────────
const WidgetForm = ({
  isEdit,
  formData,
  widgetTypes,
  handleInputChange,
  handleCreateWidget,
  handleUpdateWidget,
  handleCloseSlideout,
  services,
  practitioners,
  locations,
}) => (
  <div className="create-widget-form">
    <div className="form-sections">

      {/* Basic Settings */}
      <div className="form-section">
        <h4 className="form-section-title">Basic Settings</h4>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Widget Name *</label>
            <input
              type="text"
              className="form-input"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Enter widget name..."
              autoFocus={!isEdit}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Widget Type</label>
            <select
              className="form-input"
              value={formData.type}
              onChange={(e) => handleInputChange('type', e.target.value)}
            >
              {widgetTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Description</label>
          <textarea
            className="form-input"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Describe the widget purpose and target audience..."
            rows="3"
          />
        </div>
      </div>

      {/* Widget Content */}
      <div className="form-section">
        <h4 className="form-section-title">Widget Content</h4>
        <div className="form-group">
          <label className="form-label">Widget Title</label>
          <input
            type="text"
            className="form-input"
            value={formData.widgetTitle}
            onChange={(e) => handleInputChange('widgetTitle', e.target.value)}
            placeholder="Book Your Appointment"
          />
          <span className="form-hint">Displayed at the top of the embedded booking widget.</span>
        </div>
        <div className="form-group">
          <label className="form-label">Widget Subtitle</label>
          <input
            type="text"
            className="form-input"
            value={formData.widgetSubtitle}
            onChange={(e) => handleInputChange('widgetSubtitle', e.target.value)}
            placeholder="Choose your preferred service and time"
          />
          <span className="form-hint">Displayed below the title in the booking widget.</span>
        </div>
      </div>

      {/* Branding */}
      <div className="form-section">
        <h4 className="form-section-title">Branding & Appearance</h4>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Primary Color</label>
            <input
              type="color"
              className="form-input color-input"
              value={formData.branding.primaryColor}
              onChange={(e) => handleInputChange('branding.primaryColor', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Secondary Color</label>
            <input
              type="color"
              className="form-input color-input"
              value={formData.branding.secondaryColor}
              onChange={(e) => handleInputChange('branding.secondaryColor', e.target.value)}
            />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Logo URL</label>
          <input
            type="url"
            className="form-input"
            value={formData.branding.logoUrl}
            onChange={(e) => handleInputChange('branding.logoUrl', e.target.value)}
            placeholder="https://..."
          />
        </div>
      </div>

      {/* Features */}
      <div className="form-section">
        <h4 className="form-section-title">Features</h4>
        <div className="checkbox-grid">
          <label className="widget-form-checkbox-label">
            <input
              type="checkbox"
              checked={formData.features.socialLogin}
              onChange={(e) => handleInputChange('features.socialLogin', e.target.checked)}
            />
            <span>Guest / Social Login</span>
          </label>
          <label className="widget-form-checkbox-label">
            <input
              type="checkbox"
              checked={formData.features.paymentIntegration}
              onChange={(e) => handleInputChange('features.paymentIntegration', e.target.checked)}
            />
            <span>Payment Integration</span>
          </label>
          <label className="widget-form-checkbox-label">
            <input
              type="checkbox"
              checked={formData.features.multiStep}
              onChange={(e) => handleInputChange('features.multiStep', e.target.checked)}
            />
            <span>Multi-Step Process</span>
          </label>
          <label className="widget-form-checkbox-label">
            <input
              type="checkbox"
              checked={formData.features.notifications}
              onChange={(e) => handleInputChange('features.notifications', e.target.checked)}
            />
            <span>Email Notifications</span>
          </label>
        </div>
      </div>

      {/* Booking Rules */}
      <div className="form-section">
        <h4 className="form-section-title">Booking Rules</h4>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Advance Booking (Days)</label>
            <input
              type="number"
              className="form-input"
              value={formData.restrictions.advanceBookingDays}
              onChange={(e) => handleInputChange('restrictions.advanceBookingDays', parseInt(e.target.value))}
              min="1"
              max="365"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Cancellation Notice (Hours)</label>
            <input
              type="number"
              className="form-input"
              value={formData.restrictions.cancellationHours}
              onChange={(e) => handleInputChange('restrictions.cancellationHours', parseInt(e.target.value))}
              min="1"
              max="168"
            />
          </div>
        </div>
      </div>


      {/* Filters */}
      <div className="form-section">
        <h4 className="form-section-title">Filters</h4>
        <span className="form-hint" style={{ marginBottom: '12px', display: 'block' }}>
          Restrict this widget to show only specific items. Leave all checked to show everything.
        </span>
        <div className="filter-group-grid">
          <div className="form-group">
            <label className="form-label">Services</label>
            <MultiSelectList
              items={services}
              selectedIds={formData.allowedServices}
              onChange={(ids) => handleInputChange('allowedServices', ids)}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Practitioners</label>
            <MultiSelectList
              items={practitioners}
              selectedIds={formData.allowedPractitioners}
              onChange={(ids) => handleInputChange('allowedPractitioners', ids)}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Locations</label>
            <MultiSelectList
              items={locations}
              selectedIds={formData.allowedLocations}
              onChange={(ids) => handleInputChange('allowedLocations', ids)}
            />
          </div>
        </div>
      </div>

    </div>

    <div className="form-actions">
      <button className="btn btn-secondary" onClick={handleCloseSlideout}>
        Cancel
      </button>
      <button
        className="btn btn-primary"
        onClick={isEdit ? handleUpdateWidget : handleCreateWidget}
      >
        {isEdit ? 'Save Changes' : 'Create Widget'}
      </button>
    </div>
  </div>
);

const WidgetManagerView = ({ showNotification }) => {
  // Use shared slideout hook
  const { isOpen, title, content, width, openSlideout, closeSlideout, handleClose } = useSlideout();

  // View and filter states
  const [viewMode, setViewMode] = useState('table');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    performance: ''
  });

  // Form state for create/edit widget
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    widgetTitle: '',
    widgetSubtitle: '',
    type: 'full-booking',
    targetAudience: '',
    allowedServices: null,
    allowedPractitioners: null,
    allowedLocations: null,
    branding: {
      primaryColor: '#0e73e4',
      secondaryColor: '#4ae7cd',
      logoUrl: '',
      fontFamily: 'Inter'
    },
    features: {
      socialLogin: true,
      paymentIntegration: true,
      multiStep: true,
      notifications: true
    },
    restrictions: {
      advanceBookingDays: 30,
      cancellationHours: 24,
      minAge: 0,
      maxAge: 100
    },
    integration: {
      domains: [],
      apiKeys: '',
      webhookUrl: ''
    }
  });

  // Slideout mode: null = showing stored content, 'create' = new widget form, 'edit' = edit form
  const [slideoutMode, setSlideoutMode] = useState(null);
  const [editingWidget, setEditingWidget] = useState(null);

  // Widget state — loaded from API
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(false);

  // Filter option lists (practitioners / services / locations) for multiselect
  const [practitioners, setPractitioners] = useState([]);
  const [services, setServices] = useState([]);
  const [locations, setLocations] = useState([]);

  const fetchWidgets = useCallback(async () => {
    setLoading(true);
    setApiError(false);
    try {
      const data = await apiService.getWidgets();
      const list = Array.isArray(data) ? data : (data?.items || []);
      setWidgets(list.map(transformWidget));
    } catch {
      setApiError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWidgets();
  }, [fetchWidgets]);

  // Load practitioners / services / locations for filter multiselects (non-blocking)
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const [pracsData, svcsData, locsData] = await Promise.all([
          apiService.getPractitioners(),
          apiService.getServices(),
          apiService.getOffices(),
        ]);
        const toList = (d) => Array.isArray(d) ? d : (d?.items || []);
        setPractitioners(toList(pracsData).map(p => ({
          id: p.id,
          name: [p.firstName, p.lastName].filter(Boolean).join(' ') || p.name || 'Unknown',
        })));
        setServices(toList(svcsData).map(s => ({ id: s.id, name: s.name || 'Unknown' })));
        setLocations(toList(locsData).map(l => ({ id: l.id, name: l.name || 'Unknown' })));
      } catch {
        // Non-critical — widget form still works without filter data
      }
    };
    loadFilterOptions();
  }, []);

  // Calculate stats
  const stats = {
    totalWidgets: widgets.length,
    activeWidgets: widgets.filter(w => w.status === 'active').length,
    totalConversions: widgets.reduce((sum, w) => sum + (w.analytics?.totalConversions || 0), 0),
    totalRevenue: widgets.reduce((sum, w) => sum + (w.analytics?.revenue || 0), 0)
  };

  // Filter widgets based on search and filters
  const filteredWidgets = widgets.filter(widget => {
    const matchesSearch = widget.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         widget.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filters.status || widget.status === filters.status;
    const matchesType = !filters.type || widget.type === filters.type;
    const matchesPerformance = !filters.performance ||
      (filters.performance === 'high' && (widget.analytics?.conversionRate || 0) >= 5) ||
      (filters.performance === 'medium' && (widget.analytics?.conversionRate || 0) >= 3 && (widget.analytics?.conversionRate || 0) < 5) ||
      (filters.performance === 'low' && (widget.analytics?.conversionRate || 0) < 3);

    return matchesSearch && matchesStatus && matchesType && matchesPerformance;
  });

  // Widget type options
  const widgetTypes = [
    { value: 'full-booking', label: 'Full Booking Widget (7-step process)' },
    { value: 'quick-booking', label: 'Quick Booking Widget (3-step process)' },
    { value: 'consultation', label: 'Consultation Booking' },
    { value: 'service-specific', label: 'Service-Specific Widget' },
    { value: 'practitioner-specific', label: 'Practitioner-Specific Widget' },
    { value: 'location-specific', label: 'Location-Specific Widget' },
    { value: 'assessment', label: 'Assessment Widget' },
    { value: 'waitlist', label: 'Waitlist Widget' }
  ];

  // Reset form data on close
  const handleCloseSlideout = () => {
    setSlideoutMode(null);
    setEditingWidget(null);
    setFormData({
      name: '',
      description: '',
      widgetTitle: '',
      widgetSubtitle: '',
      type: 'full-booking',
      targetAudience: '',
      allowedServices: [],
      allowedPractitioners: [],
      allowedLocations: [],
      branding: {
        primaryColor: '#0e73e4',
        secondaryColor: '#4ae7cd',
        logoUrl: '',
        fontFamily: 'Inter'
      },
      features: {
        socialLogin: true,
        paymentIntegration: false,
        multiStep: true,
        notifications: true
      },
      restrictions: {
        advanceBookingDays: 30,
        cancellationHours: 24,
        minAge: 0,
        maxAge: 100
      },
      integration: {
        domains: [],
        apiKeys: '',
        webhookUrl: ''
      }
    });
    closeSlideout();
  };

  // Form handlers
  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleArrayChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: Array.isArray(value) ? value : [value]
    }));
  };

  // CRUD Operations
  const handleCreateWidget = async () => {
    if (!formData.name.trim()) {
      showNotification('Widget name is required', 'error');
      return;
    }

    try {
      showNotification('Creating widget...', 'info');
      const result = await apiService.createWidget({
        name: formData.name,
        description: formData.description,
        primaryColor: formData.branding.primaryColor,
        secondaryColor: formData.branding.secondaryColor,
        fontFamily: formData.branding.fontFamily || 'Inter',
        sendConfirmationEmail: formData.features.notifications,
        enablePaymentCollection: formData.features.paymentIntegration,
        allowGuestBooking: formData.features.socialLogin,
        maxAdvanceBookingDays: formData.restrictions.advanceBookingDays,
        minAdvanceBookingHours: formData.restrictions.cancellationHours,
        widgetTitle: formData.widgetTitle,
        widgetSubtitle: formData.widgetSubtitle,
        specificPractitionerIds: formData.allowedPractitioners?.length ? formData.allowedPractitioners.join(',') : null,
        specificServiceIds: formData.allowedServices?.length ? formData.allowedServices.join(',') : null,
        specificLocationIds: formData.allowedLocations?.length ? formData.allowedLocations.join(',') : null,
      });

      const newWidget = transformWidget(result);
      setWidgets(prev => [...prev, newWidget]);
      showNotification(`Widget "${formData.name}" created successfully!`, 'success');
      handleCloseSlideout();
    } catch {
      showNotification('Failed to create widget. Please try again.', 'error');
    }
  };

  const handleUpdateWidget = async () => {
    if (!formData.name.trim()) {
      showNotification('Widget name is required', 'error');
      return;
    }
    if (!editingWidget) return;

    try {
      showNotification('Saving changes...', 'info');
      const result = await apiService.updateWidget(editingWidget.id, {
        name: formData.name,
        description: formData.description,
        primaryColor: formData.branding.primaryColor,
        secondaryColor: formData.branding.secondaryColor,
        fontFamily: formData.branding.fontFamily || 'Inter',
        sendConfirmationEmail: formData.features.notifications,
        enablePaymentCollection: formData.features.paymentIntegration,
        allowGuestBooking: formData.features.socialLogin,
        maxAdvanceBookingDays: formData.restrictions.advanceBookingDays,
        minAdvanceBookingHours: formData.restrictions.cancellationHours,
        widgetTitle: formData.widgetTitle,
        widgetSubtitle: formData.widgetSubtitle,
        specificPractitionerIds: formData.allowedPractitioners?.length ? formData.allowedPractitioners.join(',') : null,
        specificServiceIds: formData.allowedServices?.length ? formData.allowedServices.join(',') : null,
        specificLocationIds: formData.allowedLocations?.length ? formData.allowedLocations.join(',') : null,
      });
      const updated = transformWidget(result);
      setWidgets(prev => prev.map(w => w.id === editingWidget.id ? updated : w));
      showNotification(`Widget "${formData.name}" updated successfully!`, 'success');
      handleCloseSlideout();
    } catch {
      showNotification('Failed to save changes. Please try again.', 'error');
    }
  };

  const handleEditWidget = (widget) => {
    const cfg = widget.configuration || {};
    setFormData({
      name: widget.name || '',
      description: widget.description || '',
      widgetTitle: widget.widgetTitle || '',
      widgetSubtitle: widget.widgetSubtitle || '',
      type: widget.type || 'full-booking',
      targetAudience: '',
      allowedServices: (cfg.allowedServices?.[0] === 'All' || !cfg.allowedServices?.length) ? null : cfg.allowedServices,
      allowedPractitioners: (cfg.allowedPractitioners?.[0] === 'All' || !cfg.allowedPractitioners?.length) ? null : cfg.allowedPractitioners,
      allowedLocations: (cfg.allowedLocations?.[0] === 'All' || !cfg.allowedLocations?.length) ? null : cfg.allowedLocations,
      branding: {
        primaryColor: cfg.branding?.primaryColor || '#0e73e4',
        secondaryColor: cfg.branding?.secondaryColor || '#4ae7cd',
        logoUrl: '',
        fontFamily: cfg.branding?.fontFamily || 'Inter'
      },
      features: {
        socialLogin: cfg.features?.socialLogin ?? true,
        paymentIntegration: cfg.features?.paymentIntegration ?? false,
        multiStep: cfg.features?.multiStep ?? true,
        notifications: cfg.features?.notifications ?? true
      },
      restrictions: {
        advanceBookingDays: cfg.restrictions?.advanceBookingDays ?? 30,
        cancellationHours: cfg.restrictions?.cancellationHours ?? 24,
        minAge: 0,
        maxAge: 100
      },
      integration: {
        domains: widget.deployment?.domains || [],
        apiKeys: '',
        webhookUrl: ''
      }
    });
    setEditingWidget(widget);
    setSlideoutMode('edit');
    openSlideout('Edit Widget', null, '750px');
  };

  const handleDeleteWidget = async (widgetId) => {
    try {
      await apiService.deleteWidget(widgetId);
      setWidgets(prev => prev.filter(w => w.id !== widgetId));
      showNotification('Widget deleted successfully!', 'success');
    } catch {
      showNotification('Failed to delete widget', 'error');
    }
  };

  const handleToggleStatus = async (widgetId) => {
    const widget = widgets.find(w => w.id === widgetId);
    if (!widget) return;

    const newIsActive = widget.status !== 'active';
    try {
      const result = await apiService.updateWidget(widgetId, {
        name: widget.name,
        isActive: newIsActive,
      });
      const updated = transformWidget(result);
      setWidgets(prev => prev.map(w => w.id === widgetId ? updated : w));
      showNotification('Widget status updated!', 'success');
    } catch {
      showNotification('Failed to update widget status', 'error');
    }
  };


  // Step configuration handlers that return to widget detail view
  const handleStepConfigSaveAndReturn = (widget) => (widgetId, stepConfiguration) => {
    // Save the configuration
    const updatedWidgets = widgets.map(w => {
      if (w.id === widgetId) {
        return {
          ...w,
          configuration: {
            ...w.configuration,
            stepOrder: stepConfiguration.stepOrder,
            customStepConfig: stepConfiguration
          },
          lastUpdated: new Date().toISOString().split('T')[0]
        };
      }
      return w;
    });

    setWidgets(updatedWidgets);

    showNotification('Widget step configuration saved successfully!', 'success');

    // Return to widget detail view
    const updatedWidget = updatedWidgets.find(w => w.id === widgetId);
    openSlideout(
      `Widget Details: ${updatedWidget.name}`,
      <WidgetDetailView widget={updatedWidget} />,
      '800px'
    );
  };

  const handleStepConfigCancelAndReturn = (widget) => () => {
    // Return to widget detail view
    openSlideout(
      `Widget Details: ${widget.name}`,
      <WidgetDetailView widget={widget} />,
      '800px'
    );
  };

  // Copy to clipboard function
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      showNotification('Copied to clipboard!', 'success');
    }).catch(() => {
      showNotification('Failed to copy to clipboard', 'error');
    });
  };



  // Widget Detail View
  const WidgetDetailView = ({ widget }) => (
    <div className="widget-detail-view">
      <div className="widget-detail-header">
        <div className="widget-detail-actions">
          <button
            className="btn btn-secondary"
            onClick={() => openSlideout(
              'Configure Widget Steps',
              <WidgetStepReorder
                widget={widget}
                onSave={handleStepConfigSaveAndReturn(widget)}
                onCancel={handleStepConfigCancelAndReturn(widget)}
                showNotification={showNotification}
              />,
              '900px',
              handleStepConfigCancelAndReturn(widget)
            )}
          >
            ⚙️ Configure Steps
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => handleEditWidget(widget)}
          >
            ✏️ Edit
          </button>
          <button
            className={`btn ${widget.status === 'active' ? 'btn-warning' : 'btn-success'}`}
            onClick={() => handleToggleStatus(widget.id)}
          >
            {widget.status === 'active' ? '⏸️ Deactivate' : '▶️ Activate'}
          </button>
        </div>
      </div>

      <div className="widget-detail-content">

        {/* Widget Title and Basic Information Section */}
        <div className="widget-detail-section widget-basic-section">
          <h4 className="section-title">{widget.name}</h4>
          <div className="section-content">
            <div className="widget-summary">
              <p className="widget-description">{widget.description}</p>
              <span className="widget-type-badge">{widget.type.toUpperCase()}</span>
            </div>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Widget ID</span>
                <span className="info-value">{widget.id}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Widget Key</span>
                <span className="info-value">{widget.widgetKey || '—'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Status</span>
                <span className={`status-badge status-${widget.status}`}>{widget.status}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Created</span>
                <span className="info-value">{widget.createdDate}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Last Modified</span>
                <span className="info-value">{widget.lastUpdated}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Widget Type</span>
                <span className="info-value">{widget.type}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Performance Analytics Section */}
        <div className="widget-detail-section widget-analytics-section">
          <h4 className="section-title">Performance Analytics</h4>
          <div className="section-content">
            <div className="analytics-grid">
              <div className="analytics-card">
                <div className="analytics-content">
                  <div className="analytics-number">{(widget.analytics?.totalViews || 0).toLocaleString()}</div>
                  <div className="analytics-label">Total Views</div>
                </div>
              </div>
              <div className="analytics-card">
                <div className="analytics-content">
                  <div className="analytics-number">{(widget.analytics?.totalConversions || 0).toLocaleString()}</div>
                  <div className="analytics-label">Conversions</div>
                </div>
              </div>
              <div className="analytics-card">
                <div className="analytics-content">
                  <div className="analytics-number">{widget.analytics?.conversionRate || 0}%</div>
                  <div className="analytics-label">Conversion Rate</div>
                </div>
              </div>
              <div className="analytics-card">
                <div className="analytics-content">
                  <div className="analytics-number">£{(widget.analytics?.revenue || 0).toLocaleString()}</div>
                  <div className="analytics-label">Revenue Generated</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Integration Section */}
        <div className="widget-detail-section widget-integration-section">
          <h4 className="section-title">Integration & Embed Options</h4>
          <div className="section-content">
            <div className="integration-options">

              {/* Shareable booking link */}
              <div className="integration-row" style={{background:'#f0f7ff',borderRadius:'8px',padding:'16px',marginBottom:'8px'}}>
                <div className="integration-info">
                  <h5 style={{color:'#1a2942'}}>🔗 Shareable Booking Link</h5>
                  <p>Send this link directly to patients, or share it on social media, in emails, or on a bio page.</p>
                </div>
                <div className="integration-code">
                  <div className="code-block">
                    <code>{`${BOOK_BASE}?id=${widget.widgetKey || widget.id}`}</code>
                    <button
                      className="copy-button"
                      onClick={() => copyToClipboard(`${BOOK_BASE}?id=${widget.widgetKey || widget.id}`)}
                      title="Copy link"
                    >
                      📋
                    </button>
                  </div>
                  <button
                    className="open-button"
                    onClick={() => window.open(`${BOOK_BASE}?id=${widget.widgetKey || widget.id}`, '_blank')}
                    style={{marginTop:'8px',fontSize:'13px',padding:'6px 14px'}}
                  >
                    Open booking page ↗
                  </button>
                </div>
              </div>

              {/* JavaScript drop-in embed — the primary embed method */}
              <div className="integration-row">
                <div className="integration-info">
                  <h5>JavaScript Embed (Recommended)</h5>
                  <p>Paste this script tag into your website's <code>&lt;head&gt;</code> or before <code>&lt;/body&gt;</code></p>
                </div>
                <div className="integration-code">
                  <div className="code-block">
                    <code>{`<script src="${API_BASE}/api/widget/loader.js" data-widget="${widget.widgetKey || widget.id}"></script>`}</code>
                    <button
                      className="copy-button"
                      onClick={() => copyToClipboard(`<script src="${API_BASE}/api/widget/loader.js" data-widget="${widget.widgetKey || widget.id}"></script>`)}
                      title="Copy to clipboard"
                    >
                      📋
                    </button>
                  </div>
                </div>
              </div>

              {/* iFrame embed */}
              <div className="integration-row">
                <div className="integration-info">
                  <h5>iFrame Embed</h5>
                  <p>Embed the booking widget inside an iframe</p>
                </div>
                <div className="integration-code">
                  <div className="code-block">
                    <code>{`<iframe src="${API_BASE}/api/widget/${widget.widgetKey || widget.id}/embed.html" width="100%" height="600" frameborder="0"></iframe>`}</code>
                    <button
                      className="copy-button"
                      onClick={() => copyToClipboard(`<iframe src="${API_BASE}/api/widget/${widget.widgetKey || widget.id}/embed.html" width="100%" height="600" frameborder="0"></iframe>`)}
                      title="Copy to clipboard"
                    >
                      📋
                    </button>
                  </div>
                </div>
              </div>

              {/* Direct link */}
              <div className="integration-row">
                <div className="integration-info">
                  <h5>Direct Link</h5>
                  <p>Share this link directly with patients</p>
                </div>
                <div className="integration-code">
                  <div className="code-block">
                    <code>{`${API_BASE}/api/widget/${widget.widgetKey || widget.id}/embed.html`}</code>
                    <button
                      className="copy-button"
                      onClick={() => copyToClipboard(`${API_BASE}/api/widget/${widget.widgetKey || widget.id}/embed.html`)}
                      title="Copy to clipboard"
                    >
                      📋
                    </button>
                    <button
                      className="open-button"
                      onClick={() => window.open(`${API_BASE}/api/widget/${widget.widgetKey || widget.id}/embed.html`, '_blank')}
                      title="Open in new tab"
                    >
                      🔗
                    </button>
                  </div>
                </div>
              </div>

              {/* WordPress shortcode */}
              <div className="integration-row">
                <div className="integration-info">
                  <h5>WordPress Shortcode</h5>
                  <p>For WordPress sites with MediBooking plugin</p>
                </div>
                <div className="integration-code">
                  <div className="code-block">
                    <code>{`[medibooking-widget key="${widget.widgetKey || widget.id}"]`}</code>
                    <button
                      className="copy-button"
                      onClick={() => copyToClipboard(`[medibooking-widget key="${widget.widgetKey || widget.id}"]`)}
                      title="Copy to clipboard"
                    >
                      📋
                    </button>
                  </div>
                </div>
              </div>

            </div>

            <div className="domain-restrictions">
              <h5>Allowed Domains</h5>
              <div className="domain-list">
                {widget.deployment?.domains?.length > 0 ?
                  widget.deployment.domains.map((domain, index) => (
                    <span key={index} className="domain-tag">{domain}</span>
                  )) :
                  <span className="domain-none">No domain restrictions</span>
                }
              </div>
            </div>
          </div>
        </div>

        {/* Configuration Section */}
        <div className="widget-detail-section widget-config-section">
          <h4 className="section-title">Configuration Settings</h4>
          <div className="section-content">
            <div className="config-grid">
              <div className="config-item">
                <span className="config-label">Theme</span>
                <span className="config-value config-value-theme">{widget.configuration?.branding?.primaryColor || 'Default'}</span>
              </div>
              <div className="config-item">
                <span className="config-label">Allowed Services</span>
                <span className="config-value">{widget.configuration?.allowedServices?.join(', ') || 'All'}</span>
              </div>
              <div className="config-item">
                <span className="config-label">Allowed Practitioners</span>
                <span className="config-value">{widget.configuration?.allowedPractitioners?.join(', ') || 'All'}</span>
              </div>
              <div className="config-item">
                <span className="config-label">Allowed Locations</span>
                <span className="config-value">{widget.configuration?.allowedLocations?.join(', ') || 'All'}</span>
              </div>
              <div className="config-item">
                <span className="config-label">Guest Booking</span>
                <span className={`config-value ${widget.configuration?.features?.socialLogin ? 'config-enabled' : 'config-disabled'}`}>
                  {widget.configuration?.features?.socialLogin ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className="config-item">
                <span className="config-label">Payment Integration</span>
                <span className={`config-value ${widget.configuration?.features?.paymentIntegration ? 'config-enabled' : 'config-disabled'}`}>
                  {widget.configuration?.features?.paymentIntegration ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Live Preview Section */}
        <div className="widget-detail-section widget-preview-section">
          <h4 className="section-title">Live Preview</h4>
          <div className="section-content">
            <div className="preview-container">
              <div className="preview-header">
                <h5 className="preview-title">{widget.widgetTitle || 'Book Your Appointment'}</h5>
                <span className="preview-badge">LIVE PREVIEW</span>
              </div>
              <div className="preview-widget">
                <div className="preview-steps">
                  <div className="preview-step active">
                    <span className="step-number">1</span>
                    <span className="step-text">Select Service</span>
                  </div>
                  <div className="preview-step">
                    <span className="step-number">2</span>
                    <span className="step-text">Choose Practitioner</span>
                  </div>
                  <div className="preview-step">
                    <span className="step-number">3</span>
                    <span className="step-text">Pick Date & Time</span>
                  </div>
                  <div className="preview-step">
                    <span className="step-number">4</span>
                    <span className="step-text">Your Details</span>
                  </div>
                </div>
                <div className="preview-content">
                  <p className="preview-description">This is how your booking widget will appear to users on your website.</p>
                  <button className="preview-action-btn" style={{
                    backgroundColor: widget.configuration?.branding?.secondaryColor || '#4ae7cd'
                  }}>
                    Start Booking Process
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );

  // Main render
  return (
    <div className="widget-manager-view">
      {/* Page Header */}
      <PageHeader pageKey="widgets">
        <button
          className="btn btn-primary"
          onClick={() => {
          setSlideoutMode('create');
          openSlideout('Create New Widget', null, '750px');
        }}
        >
          <span className="icon icon--add icon--success"></span>
          Create Widget
        </button>
      </PageHeader>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-number">{loading ? '—' : stats.totalWidgets}</div>
            <div className="stat-label">Total Widgets</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-number">{loading ? '—' : stats.activeWidgets}</div>
            <div className="stat-label">Active Widgets</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-number">{loading ? '—' : stats.totalConversions.toLocaleString()}</div>
            <div className="stat-label">Total Bookings</div>
          </div>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="card">
        <div className="card-header">
          <div className="card-header-content">
            <h3 className="card-title">Widgets</h3>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="filters-bar">
          <div className="search-section">
            <input
              type="text"
              className="search-input"
              placeholder="Search widgets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filters-section">
            <select
              className="filter-select"
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <select
              className="filter-select"
              value={filters.type}
              onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
            >
              <option value="">All Types</option>
              {widgetTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            <select
              className="filter-select"
              value={filters.performance}
              onChange={(e) => setFilters(prev => ({ ...prev, performance: e.target.value }))}
            >
              <option value="">All Performance</option>
              <option value="high">High (5%+ conversion)</option>
              <option value="medium">Medium (3-5% conversion)</option>
              <option value="low">Low (&lt;3% conversion)</option>
            </select>
          </div>
          <div className="view-controls">
            <button
              className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
            >
              📋 Table
            </button>
            <button
              className={`view-btn ${viewMode === 'cards' ? 'active' : ''}`}
              onClick={() => setViewMode('cards')}
            >
              🗂️ Cards
            </button>
          </div>
        </div>

        {/* Widget List */}
        <div className="card-content">
          {loading ? (
            <div className="api-error-state">
              <div className="loading-spinner"></div>
              <p>Loading widgets...</p>
            </div>
          ) : apiError ? (
            <div className="api-error-state">
              <div className="api-error-state__icon">⚠️</div>
              <h3>Could not load widgets</h3>
              <p>There was a problem connecting to the server. Please check your connection and try again.</p>
              <button className="btn btn-primary" onClick={fetchWidgets}>Retry</button>
            </div>
          ) : viewMode === 'table' ? (
            <div className="material-table-container">
              <div className="material-table-header">
                <h3 className="material-table-title">Widget Overview</h3>
                <span className="material-table-meta">Showing {filteredWidgets.length} widgets</span>
              </div>

              <table className="material-table">
                <thead>
                  <tr>
                    <th className="material-th">Widget Name</th>
                    <th className="material-th">Status</th>
                    <th className="material-th">Views</th>
                    <th className="material-th">Conversions</th>
                    <th className="material-th">Conversion Rate</th>
                    <th className="material-th">Last Updated</th>
                    <th className="material-th">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredWidgets.map(widget => (
                    <tr key={widget.id} className="material-tr">
                      <td className="material-cell">
                        <div className="material-widget-cell">
                          <div className="material-widget-info">
                            <div className="material-body-large">{widget.name}</div>
                            <div className="material-body-small material-text-secondary">{widget.description}</div>
                          </div>
                        </div>
                      </td>
                      <td className="material-cell">
                        <span className={`material-chip--${widget.status === 'active' ? 'success' : 'error'}`}>
                          {widget.status}
                        </span>
                      </td>
                      <td className="material-cell">
                        <span className="material-body-medium">{(widget.analytics?.totalViews || 0).toLocaleString()}</span>
                      </td>
                      <td className="material-cell">
                        <span className="material-body-medium">{(widget.analytics?.totalConversions || 0).toLocaleString()}</span>
                      </td>
                      <td className="material-cell">
                        <span className={`material-body-medium conversion-rate ${
                          (widget.analytics?.conversionRate || 0) >= 5 ? 'high' :
                          (widget.analytics?.conversionRate || 0) >= 3 ? 'medium' : 'low'
                        }`}>
                          {widget.analytics?.conversionRate || 0}%
                        </span>
                      </td>
                      <td className="material-cell">
                        <span className="material-body-medium">{widget.lastUpdated}</span>
                      </td>
                      <td className="actions-cell">
                        <div className="action-icons">
                          <button
                            className="action-icon action-icon--view"
                            onClick={() => openSlideout(
                              `Widget Details: ${widget.name}`,
                              <WidgetDetailView widget={widget} />,
                              '800px'
                            )}
                            data-tooltip="View Details"
                          ></button>
                          <button
                            className="action-icon action-icon--edit"
                            onClick={() => handleEditWidget(widget)}
                            data-tooltip="Edit Widget"
                          ></button>
                          <button
                            className="action-icon action-icon--toggle"
                            onClick={() => handleToggleStatus(widget.id)}
                            data-tooltip={widget.status === 'active' ? 'Deactivate' : 'Activate'}
                          ></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredWidgets.length === 0 && widgets.length > 0 && (
                <div className="empty-state">
                  <p>No widgets match your current filters.</p>
                </div>
              )}

              {widgets.length === 0 && (
                <div className="empty-state">
                  <div className="empty-state-icon">
                    <span className="icon icon--export icon--default" style={{fontSize: '48px', width: '48px', height: '48px'}}></span>
                  </div>
                  <h3>No widgets yet</h3>
                  <p>Create your first booking widget to get started.</p>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
          setSlideoutMode('create');
          openSlideout('Create New Widget', null, '750px');
        }}
                  >
                    <span className="icon icon--add icon--success"></span>
                    Create Your First Widget
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="widgets-grid">
              {filteredWidgets.map(widget => (
                <div key={widget.id} className="widget-card">
                  <div className="widget-card-header">
                    <div className="widget-card-title">
                      <h4>{widget.name}</h4>
                      <span className={`status-badge status-${widget.status}`}>
                        {widget.status}
                      </span>
                    </div>
                  </div>

                  <div className="widget-card-description">
                    {widget.description}
                  </div>

                  <div className="widget-card-preview" style={{
                    backgroundColor: widget.configuration?.branding?.primaryColor || '#0e73e4',
                    borderRadius: '6px',
                    padding: '15px',
                    margin: '15px 0',
                    color: 'white',
                    textAlign: 'center',
                    minHeight: '100px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: '600' }}>
                      {widget.name}
                    </div>
                  </div>

                  <div className="widget-card-stats">
                    <div className="widget-stat">
                      <div className="widget-stat-number">{(widget.analytics?.totalViews || 0).toLocaleString()}</div>
                      <div className="widget-stat-label">Views</div>
                    </div>
                    <div className="widget-stat">
                      <div className="widget-stat-number">{widget.analytics?.totalConversions || 0}</div>
                      <div className="widget-stat-label">Conversions</div>
                    </div>
                    <div className="widget-stat">
                      <div className={`widget-stat-number conversion-rate ${
                        (widget.analytics?.conversionRate || 0) >= 5 ? 'high' :
                        (widget.analytics?.conversionRate || 0) >= 3 ? 'medium' : 'low'
                      }`}>
                        {widget.analytics?.conversionRate || 0}%
                      </div>
                      <div className="widget-stat-label">Conv. Rate</div>
                    </div>
                  </div>

                  <div className="widget-card-actions">
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => openSlideout(
                        `Widget Details: ${widget.name}`,
                        <WidgetDetailView widget={widget} />,
                        '800px'
                      )}
                    >
                      👁️ View Details
                    </button>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleEditWidget(widget)}
                    >
                      <span className="icon icon--edit icon--default"></span>
                      Edit
                    </button>
                    <button
                      className={`btn btn-sm ${widget.status === 'active' ? 'btn-warning' : 'btn-success'}`}
                      onClick={() => handleToggleStatus(widget.id)}
                    >
                      {widget.status === 'active' ? '⏸️ Pause' : '▶️ Activate'}
                    </button>
                  </div>

                  <div className="widget-card-footer">
                    <small className="text-muted">
                      Updated: {widget.lastUpdated}
                    </small>
                  </div>
                </div>
              ))}

              {filteredWidgets.length === 0 && (
                <div className="empty-state">
                  <div className="empty-state-icon">
                    <span className="icon icon--export icon--default" style={{fontSize: '48px', width: '48px', height: '48px'}}></span>
                  </div>
                  <h3>No widgets found</h3>
                  <p>Try adjusting your search criteria or create a new widget.</p>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
          setSlideoutMode('create');
          openSlideout('Create New Widget', null, '750px');
        }}
                  >
                    <span className="icon icon--add icon--success"></span>
                    Create Your First Widget
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <SlideoutPanel
        isOpen={isOpen}
        onClose={handleClose}
        title={title}
        width={width}
      >
        {slideoutMode === 'create' ? (
          <WidgetForm
            isEdit={false}
            formData={formData}
            widgetTypes={widgetTypes}
            handleInputChange={handleInputChange}
            handleCreateWidget={handleCreateWidget}
            handleUpdateWidget={handleUpdateWidget}
            handleCloseSlideout={handleCloseSlideout}
            services={services}
            practitioners={practitioners}
            locations={locations}
          />
        ) : slideoutMode === 'edit' ? (
          <WidgetForm
            isEdit={true}
            formData={formData}
            widgetTypes={widgetTypes}
            handleInputChange={handleInputChange}
            handleCreateWidget={handleCreateWidget}
            handleUpdateWidget={handleUpdateWidget}
            handleCloseSlideout={handleCloseSlideout}
            services={services}
            practitioners={practitioners}
            locations={locations}
          />
        ) : (
          content
        )}
      </SlideoutPanel>
    </div>
  );
};

export default WidgetManagerView;
